import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import api from "@/services/api";
import { Input } from "@/components/ui/input";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PageHeader from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLocationData } from "@/lib/locations";
import { useCourses } from "@/hooks/use-courses";
import { ArrowLeft, Pencil } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const EditMemberDetails = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [app, setApp] = useState<any>(null);
  const [studentName, setStudentName] = useState<string>("");
  const [nameInput, setNameInput] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ qualification: "", city: "", country: "", admission_year: "", gender: "" });

  const loc = useLocationData();
  const cityOpts = useMemo(() => loc?.citiesForCountry(form.country) ?? [], [loc, form.country]);
  const { courses } = useCourses();
  const courseOpts = useMemo(() => courses.map((c) => ({ value: c.name, label: c.name })), [courses]);
  const currentYear = new Date().getFullYear();
  const yearOpts = useMemo(() => Array.from({ length: currentYear - 1990 + 1 }, (_, i) => currentYear - i), [currentYear]);

  const backToStudent = () => navigate(`/growlearn/students/${userId}`);

  useEffect(() => {
    if (!userId) return;
    Promise.all([
      supabase.from("applications").select("*").eq("user_id", userId).order("created_at", { ascending: false }).limit(1),
      supabase.from("profiles").select("full_name").eq("user_id", userId).maybeSingle(),
    ]).then(([{ data: apps }, { data: prof }]) => {
      const a = apps?.[0] || null;
      setApp(a);
      setStudentName(prof?.full_name || "");
      setNameInput(prof?.full_name || "");
      if (a) {
        setForm({
          qualification: a.qualification || "",
          city: a.city || "",
          country: a.country || "",
          admission_year: a.admission_year ? String(a.admission_year) : "",
          gender: a.gender || "",
        });
      }
      setLoading(false);
    });
  }, [userId]);

  const save = async () => {
    if (!app) return;
    const newName = nameInput.trim().replace(/\s+/g, " ");
    if (!newName) {
      toast({ title: "Name required", description: "Please enter the student's full name.", variant: "destructive" });
      return;
    }
    setSaving(true);
    if (newName !== studentName) {
      try {
        await api.patch(`/students/${userId}/name`, { full_name: newName });
      } catch (e: any) {
        setSaving(false);
        toast({ title: "Error", description: e?.response?.data?.error || e.message || "Could not update the name.", variant: "destructive" });
        return;
      }
    }
    const { error } = await supabase
      .from("applications")
      .update({
        qualification: form.qualification || null,
        city: form.city || null,
        country: form.country || null,
        admission_year: form.admission_year ? parseInt(form.admission_year) : null,
        gender: form.gender || null,
      })
      .eq("id", app.id);
    setSaving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Details updated", description: "Member details saved successfully." });
    backToStudent();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          title="Edit Member Details"
          crumbLabel="Student"
          crumbTo={`/growlearn/students/${userId}`}
          subtitle={studentName ? `Editing ${studentName}` : undefined}
        />

        <button
          onClick={backToStudent}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Student
        </button>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : !app ? (
          <Card className="glass-premium shadow-sm">
            <CardContent className="py-16 text-center text-muted-foreground">No application found for this student.</CardContent>
          </Card>
        ) : (
          <Card className="glass-premium shadow-sm">
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Pencil className="h-4 w-4" />
                </span>
                Edit Member Details
              </CardTitle>
              <p className="text-sm text-muted-foreground pl-10 -mt-1">Update name, qualification, location &amp; year</p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid gap-5 lg:grid-cols-2 items-start">
                {/* Full name — full width. Fixes spelling mistakes from registration. */}
                <div className="space-y-1.5 lg:col-span-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Full Name</p>
                  <Input
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Student's full name"
                    maxLength={150}
                    className="h-10"
                  />
                  <p className="text-xs text-muted-foreground">Shown on the certificate, Letter of Good Standing and emails.</p>
                </div>
                {/* Qualification — full width */}
                <div className="space-y-1.5 lg:col-span-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Qualification</p>
                  <Combobox
                    options={courseOpts}
                    value={form.qualification}
                    onChange={(v) => setForm({ ...form, qualification: v })}
                    placeholder="Select qualification"
                    searchPlaceholder="Search…"
                    emptyText="No courses — add them in Settings."
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Country</p>
                  <Combobox
                    options={loc?.countries ?? []}
                    value={form.country}
                    onChange={(v) => setForm({ ...form, country: v, city: "" })}
                    placeholder={loc ? "Select country" : "Loading…"}
                    searchPlaceholder="Search country…"
                    disabled={!loc}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">City / Town</p>
                  <Combobox
                    options={cityOpts}
                    value={form.city}
                    onChange={(v) => setForm({ ...form, city: v })}
                    placeholder={form.country ? "Select city" : "Select country first"}
                    searchPlaceholder="Search city…"
                    disabled={!form.country}
                    allowCustom
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Admission Year</p>
                  <Select value={form.admission_year} onValueChange={(v) => setForm({ ...form, admission_year: v })}>
                    <SelectTrigger className="h-10 max-w-[160px]">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {yearOpts.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  {/* Drives the he/she pronoun in the Letter of Good Standing. */}
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Gender</p>
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger className="h-10 max-w-[160px]">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-8 pt-5 border-t border-border/40">
                <Button onClick={save} disabled={saving} className="gradient-warm text-white border-0 h-10 px-6 font-semibold">
                  {saving ? "Saving…" : "Save Changes"}
                </Button>
                <Button variant="outline" onClick={backToStudent} disabled={saving} className="h-10 px-6">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EditMemberDetails;
